import type { LearningPath, Unit, Lesson, Question, DiagramData, DiagramPlayer, DiagramRoute, DragDropData } from './lessons';

// Re-export types for convenience
export type { LearningPath, Unit, Lesson };

// Soccer field diagram helper — positions are on a 100x80 SVG viewBox
// Y=70 is the back line, Y=10 is the opponent's goal

export const soccerLearningPaths: LearningPath[] = [
  {
    id: 'soccer-basics',
    difficulty: 'beginner',
    title: 'The Basics',
    description: 'Learn positions, rules, and how the beautiful game works.',
    color: 'primary',
    units: [
      {
        id: 'soccer-formations-101',
        title: 'Formations 101',
        icon: '⚽',
        requiredXp: 0,
        lessons: [
          {
            id: 'soccer-what-is-formation',
            title: 'What Is a Formation?',
            description: 'How 11 players organize on the pitch.',
            conceptText: [
              'A formation describes how the 10 outfield players are arranged on the pitch (the goalkeeper is always assumed). It\'s written in numbers from defense to attack.',
              'For example, 4-4-2 means 4 defenders, 4 midfielders, and 2 forwards. The numbers always add up to 10 (plus the GK).',
              'Formations aren\'t rigid — players move fluidly during play. Think of a formation as the team\'s starting shape and how they organize when they don\'t have the ball.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'sf-tf1',
                type: 'true-false',
                question: 'A goalkeeper can pick up a ball passed to them by their own teammate with their hands.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! The back-pass rule prohibits the GK from handling a deliberate pass from a teammate\'s foot. It results in an indirect free kick.'
              },
              {
                id: 'sf-q1',
                type: 'multiple-choice',
                question: 'In a 4-3-3 formation, what does the first number represent?',
                options: ['Midfielders', 'Defenders', 'Forwards', 'Goalkeepers'],
                correctIndex: 1,
                explanation: 'Formation numbers read from defense to attack. The first number is always the defensive line — in 4-3-3, that\'s 4 defenders.'
              },
              {
                id: 'sf-scenario1',
                type: 'scenario',
                question: 'The ball crosses the sideline in soccer. A player from the team that didn\'t touch it last steps up to the line. What happens next?',
                options: ['Throw-in', 'Corner kick', 'Goal kick'],
                correctIndex: 0,
                explanation: 'When the ball crosses the sideline (touchline), play restarts with a throw-in by the team that did NOT touch it last.'
              },
              {
                id: 'sf-q2',
                type: 'multiple-choice',
                question: 'A team plays a 3-5-2 formation. How many outfield players does that account for?',
                options: ['9', '10', '11', '12'],
                correctIndex: 1,
                explanation: '3+5+2 = 10. Formation numbers always add up to 10 outfield players. The goalkeeper is the 11th player and is always assumed.'
              },
              {
                id: 'sf-tf2',
                type: 'true-false',
                question: 'Formation numbers in soccer always add up to 11.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! Formation numbers add up to 10 — the goalkeeper is always assumed and not counted in the formation numbers.'
              },
              {
                id: 'sf-q3',
                type: 'fix-mistake',
                question: '"A formation tells you exactly where each player will be during the entire match." What\'s wrong?',
                options: ['Nothing, formations are fixed positions', 'Formations are a starting shape — players move fluidly and interchange during play', 'Formations only apply to the first half', 'Only defenders follow the formation'],
                correctIndex: 1,
                explanation: 'Formations describe the team\'s default shape, especially when defending. In possession, players constantly rotate, drift, and interchange positions.'
              }
            ]
          },
          {
            id: 'soccer-442',
            title: 'The 4-4-2',
            description: 'The classic formation that dominated world football.',
            conceptText: [
              'The 4-4-2 is the most traditional formation in football. It features a flat back four (2 center-backs and 2 full-backs), 4 midfielders, and 2 strikers.',
              'Its strength is balance — two banks of four create a compact, hard-to-break-down defensive shape. The two strikers can press the opposition\'s center-backs.',
              'Teams like Atlético Madrid under Simeone and Leicester City\'s 2015-16 title-winning team showed the 4-4-2 can still compete at the highest level with discipline and quick transitions.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sf-442-q1',
                type: 'diagram-identify',
                question: 'Study the formation below. What formation is this team playing?',
                diagram: {
                  title: '',
                  sport: 'soccer',
                  players: [
                    { x: 50, y: 75, type: 'offense', label: 'GK' },
                    { x: 20, y: 60, type: 'offense', label: 'LB' },
                    { x: 40, y: 62, type: 'offense', label: 'CB' },
                    { x: 60, y: 62, type: 'offense', label: 'CB' },
                    { x: 80, y: 60, type: 'offense', label: 'RB' },
                    { x: 15, y: 42, type: 'offense', label: 'LM' },
                    { x: 38, y: 44, type: 'offense', label: 'CM' },
                    { x: 62, y: 44, type: 'offense', label: 'CM' },
                    { x: 85, y: 42, type: 'offense', label: 'RM' },
                    { x: 38, y: 22, type: 'offense', label: 'ST' },
                    { x: 62, y: 22, type: 'offense', label: 'ST' },
                  ],
                  routes: []
                },
                options: ['4-3-3', '4-4-2', '3-5-2', '4-2-3-1'],
                correctIndex: 1,
                explanation: 'The 4-4-2: a flat back four, a midfield four (two wide, two central), and a strike partnership of two forwards.'
              },
              {
                id: 'sf-442-q2',
                type: 'multiple-choice',
                question: 'What tactical advantage does a 4-4-2 create when the team is defending?',
                options: ['An extra attacker in transition', 'Two compact banks of four that are hard to play through', 'A numerical advantage in the midfield triangle', 'An overload on the flanks with wingbacks'],
                correctIndex: 1,
                explanation: 'The 4-4-2 creates two lines of four (defense + midfield), forming a compact block that\'s very difficult to penetrate centrally.'
              },
              {
                id: 'sf-442-q3',
                type: 'drag-drop',
                question: 'Build a 4-4-2 formation. Place the GK at the back, 4 defenders across, 4 midfielders in a line, and 2 strikers up top.',
                options: [],
                correctIndex: 0,
                explanation: 'In a 4-4-2: GK at the back, 4 defenders across, 4 midfielders in a line, and 2 strikers up front.',
                hint: 'The goalkeeper is the deepest position. Defenders line up in front of them, then midfielders, then strikers closest to the opposition goal.',
                dragDropData: {
                  sport: 'soccer',
                  slots: [
                    { id: 'gk', label: '?', x: 80, y: 88 },
                    { id: 'lb', label: '?', x: 30, y: 72 },
                    { id: 'cb1', label: '?', x: 62, y: 74 },
                    { id: 'cb2', label: '?', x: 98, y: 74 },
                    { id: 'rb', label: '?', x: 130, y: 72 },
                    { id: 'lm', label: '?', x: 22, y: 50 },
                    { id: 'cm1', label: '?', x: 58, y: 52 },
                    { id: 'cm2', label: '?', x: 102, y: 52 },
                    { id: 'rm', label: '?', x: 138, y: 50 },
                    { id: 'st1', label: '?', x: 62, y: 25 },
                    { id: 'st2', label: '?', x: 98, y: 25 },
                  ],
                  pieces: ['GK', 'LB', 'CB', 'CB', 'RB', 'LM', 'CM', 'CM', 'RM', 'ST', 'ST'],
                  correctMapping: {
                    gk: 'GK', lb: 'LB', cb1: 'CB', cb2: 'CB', rb: 'RB',
                    lm: 'LM', cm1: 'CM', cm2: 'CM', rm: 'RM',
                    st1: 'ST', st2: 'ST'
                  }
                }
              }
            ]
          },
          {
            id: 'soccer-433',
            title: 'The 4-3-3',
            description: 'The attacking formation favored by top European clubs.',
            conceptText: [
              'The 4-3-3 uses 4 defenders, 3 midfielders (often one defensive "pivot" and two more advanced), and 3 forwards (a center forward flanked by two wingers).',
              'This formation dominates possession football. The midfield triangle allows for short passing combinations, while the three forwards stretch the opposition\'s defense wide.',
              'Many of the world\'s top clubs have used variations of the 4-3-3 to great success. It\'s considered the most popular formation in modern elite football due to its balance of attack and control.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sf-433-q1',
                type: 'multiple-choice',
                question: 'What is the typical midfield shape in a 4-3-3?',
                options: ['A flat line of three', 'A triangle with one deep and two advanced', 'A diamond with four players', 'Two holding and one wide'],
                correctIndex: 1,
                explanation: 'The 4-3-3 typically uses a midfield triangle — one deeper "pivot" or holding midfielder with two more advanced midfielders ahead.'
              },
              {
                id: 'sf-433-q2',
                type: 'diagram-identify',
                question: 'Identify this formation from the pitch diagram.',
                diagram: {
                  title: '',
                  sport: 'soccer',
                  players: [
                    { x: 50, y: 75, type: 'offense', label: 'GK' },
                    { x: 20, y: 60, type: 'offense', label: 'LB' },
                    { x: 40, y: 62, type: 'offense', label: 'CB' },
                    { x: 60, y: 62, type: 'offense', label: 'CB' },
                    { x: 80, y: 60, type: 'offense', label: 'RB' },
                    { x: 50, y: 48, type: 'offense', label: 'DM' },
                    { x: 35, y: 38, type: 'offense', label: 'CM' },
                    { x: 65, y: 38, type: 'offense', label: 'CM' },
                    { x: 20, y: 20, type: 'offense', label: 'LW' },
                    { x: 50, y: 18, type: 'offense', label: 'ST' },
                    { x: 80, y: 20, type: 'offense', label: 'RW' },
                  ],
                  routes: []
                },
                options: ['4-4-2', '3-4-3', '4-3-3', '4-2-3-1'],
                correctIndex: 2,
                explanation: 'The 4-3-3: four defenders, a midfield triangle (one deep, two advanced), and three forwards spread across the pitch.'
              },
              {
                id: 'sf-433-q3',
                type: 'multiple-choice',
                question: 'Compared to a 4-4-2, what is the main trade-off of using a 4-3-3?',
                options: ['One fewer midfielder but an extra forward creates more width in attack', 'One fewer defender makes the team more vulnerable to counter-attacks', 'The wingers must do all the defensive work alone', 'There is no trade-off — 4-3-3 is always better'],
                correctIndex: 0,
                explanation: 'The 4-3-3 sacrifices a midfielder for an extra forward, gaining attacking width and stretching defenses but having one fewer body in the midfield battle.'
              }
            ]
          }
        ]
      },
      {
        id: 'soccer-positions',
        title: 'Key Positions',
        icon: '👥',
        requiredXp: 50,
        lessons: [
          {
            id: 'soccer-goalkeeper',
            title: 'The Goalkeeper & Defenders',
            description: 'The last line and the back four.',
            conceptText: [
              'The goalkeeper (GK) is the only player who can use their hands — but only inside the penalty area. Modern GKs also need to be good with their feet to play out from the back.',
              'Center-backs (CB) are the central defenders. They need to be strong in the air, good at tackling, and able to read the game. Most formations use two CBs, some use three.',
              'Full-backs (LB/RB) defend the flanks but in modern football they\'re expected to attack too. The role has evolved dramatically, with some full-backs becoming key creative players from defense.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sp-q1',
                type: 'multiple-choice',
                question: 'A goalkeeper picks up the ball with their hands just outside the penalty area. What happens?',
                options: ['Play continues — the GK can use hands in their own half', 'A free kick is awarded to the opposing team', 'The goal kick is retaken', 'A penalty kick is given'],
                correctIndex: 1,
                explanation: 'The goalkeeper can only handle the ball inside the penalty area. Outside it, handling results in a direct free kick for the opposition.'
              },
              {
                id: 'sp-q2',
                type: 'fix-mistake',
                question: '"Full-backs should focus entirely on attacking — their main job is to get crosses into the box." What\'s wrong?',
                options: ['Nothing, full-backs are primarily attackers', 'Full-backs must balance attacking and defending — defensive duties come first', 'Full-backs never cross the halfway line', 'Full-backs only play in a 3-at-the-back system'],
                correctIndex: 1,
                explanation: 'While modern full-backs contribute heavily in attack, their primary responsibility is still defensive. The best full-backs balance both — overlapping in attack and recovering to defend.'
              },
              {
                id: 'sp-q3',
                type: 'multiple-choice',
                question: 'What does "playing out from the back" mean in modern football?',
                options: ['Defending with a deep block near your own goal', 'Building attacks through short passes starting from the goalkeeper and defenders', 'Using long balls to bypass the midfield', 'Pressing the opponent high up the pitch'],
                correctIndex: 1,
                explanation: 'Playing out from the back means building possession through short passes starting from the GK and CBs, rather than launching long balls forward. It requires technically skilled defenders.'
              }
            ]
          },
          {
            id: 'soccer-midfield',
            title: 'Midfield Roles',
            description: 'The engine room — DM, CM, AM, and the #10.',
            conceptText: [
              'The defensive midfielder (DM/CDM) sits in front of the defense, breaking up play and recycling possession. This role can be a "destroyer" who wins the ball back, or a "controller" who dictates tempo.',
              'Central midfielders (CM) are the engine. They link defense to attack, cover ground, tackle, pass, and sometimes score. Box-to-box midfielders contribute at both ends of the pitch.',
              'The #10 (attacking midfielder/playmaker) plays behind the strikers and creates chances. This is the creative hub — the role demands vision, technique, and the ability to unlock defenses with a single pass.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sm-q1',
                type: 'multiple-choice',
                question: 'A team\'s DM intercepts a pass, then immediately plays a simple ball to a teammate to start an attack. Which DM style is this?',
                options: ['A "destroyer" who wins the ball aggressively', 'A "controller" who dictates the tempo of play', 'A box-to-box midfielder joining the attack', 'A #10 dropping deep to collect the ball'],
                correctIndex: 1,
                explanation: 'A "controller" DM wins the ball and recycles possession calmly, dictating the team\'s rhythm. A "destroyer" focuses more on aggressive ball-winning and breaking up play.'
              },
              {
                id: 'sm-q2',
                type: 'identify',
                question: 'A player who covers every blade of grass, tackles in their own box, then arrives in the opponent\'s box to score. What type of midfielder is this?',
                options: ['Defensive midfielder', 'Box-to-box midfielder', 'Playmaker (#10)', 'Deep-lying playmaker'],
                correctIndex: 1,
                explanation: 'A box-to-box midfielder contributes at both ends of the pitch — defending in their own penalty box and attacking in the opponent\'s.'
              },
              {
                id: 'sm-q3',
                type: 'multiple-choice',
                question: 'Why has the traditional #10 role become less common in modern football?',
                options: ['Teams no longer need creative players', 'High pressing makes it hard for a player who doesn\'t defend to justify their position', 'The offside rule changed to eliminate the position', 'Goalkeepers now take over the creative role'],
                correctIndex: 1,
                explanation: 'Modern pressing systems demand every player contributes defensively. A pure #10 who doesn\'t press or track back can be a defensive liability against high-pressing teams.'
              }
            ]
          }
        ]
      },
      {
        id: 'soccer-core-skills',
        title: 'Core Skills',
        icon: '🛠️',
        requiredXp: 100,
        lessons: [
          {
            id: 'soccer-defending-1v1',
            title: 'Defending 1v1',
            description: 'Stop an attacker head-on with patience, angles, and timing. (~8 min)',
            conceptText: [
              'Defending 1v1 is about controlling the attacker, not diving in. Approach at an angle to force them toward the sideline or their weaker foot.',
              'Stay low, keep your arms out for balance, and watch the ball — not the player\'s hips or eyes. Patience wins duels; a mistimed step gets you beaten.',
              'Make your tackle when the attacker takes a heavy touch or looks down at the ball. A well-timed poke or block is usually better than a wild lunge.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sd1-q1',
                type: 'multiple-choice',
                question: 'What is the first priority when defending 1v1?',
                options: ['Win the ball immediately', 'Force the attacker toward the sideline or weak foot', 'Stay directly in front and mirror every step', 'Call for a teammate to foul them'],
                correctIndex: 1,
                explanation: 'Good 1v1 defense starts with angles. By forcing the attacker wide or onto their weaker foot, you reduce their dangerous options.'
              },
              {
                id: 'sd1-q2',
                type: 'true-false',
                question: 'A defender should always watch the attacker\'s eyes to predict their next move.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! Defenders should watch the ball, not the eyes. The ball tells you when the touch is heavy and when to tackle.'
              },
              {
                id: 'sd1-q3',
                type: 'scenario',
                question: 'An attacker is dribbling at you down the middle. You angle your body to steer them toward the left sideline. What is this technique called?',
                options: ['Channeling / forcing', 'Pressing trigger', 'Offside trap', 'Shadow defending'],
                correctIndex: 0,
                explanation: 'Channeling (or forcing) means using your body position to direct the attacker where you want them to go — usually away from goal.'
              }
            ]
          },
          {
            id: 'soccer-runs-in-behind',
            title: 'Making Runs in Behind',
            description: 'Time your movement to exploit space behind the back line. (~8 min)',
            conceptText: [
              'A run in behind is an attacking movement where you sprint into the space behind the opponent\'s defensive line, hoping a teammate plays the ball into that channel.',
              'Timing is everything. Start your run while the passer is still looking at you, and accelerate as the ball is played. Going too early leads to offside; too late and the gap closes.',
              'Runs in behind stretch the defense vertically. Even when the pass doesn\'t come, your movement can create space for teammates to receive between the lines.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'srb-q1',
                type: 'multiple-choice',
                question: 'Why is timing the most important part of a run in behind?',
                options: ['Because the pass is always long', 'Because starting too early causes offside and too late lets the gap close', 'Because the goalkeeper can intercept', 'Because teammates rarely look up'],
                correctIndex: 1,
                explanation: 'A perfectly timed run stays onside until the ball is played, then bursts into the open space before defenders can recover.'
              },
              {
                id: 'srb-q2',
                type: 'fix-mistake',
                question: '"You should always make your run in behind as early as possible to beat the defender." What\'s wrong?',
                options: ['Nothing — early runs are always best', 'Going too early leads to offside; the run must be timed with the pass', 'Early runs are only for wingers', 'Defenders cannot play offside'],
                correctIndex: 1,
                explanation: 'The run must be synchronized with the pass. Too early = offside flag. Too late = the defensive line recovers.'
              },
              {
                id: 'srb-q3',
                type: 'scenario',
                question: 'A striker makes a diagonal run behind the right center-back. The pass is played over the top. What is the striker trying to create?',
                options: ['A corner kick', 'A 1v1 with the goalkeeper', 'A throw-in', 'A free kick'],
                correctIndex: 1,
                explanation: 'A well-timed run in behind isolates the striker against the goalkeeper — one of the most dangerous attacking situations.'
              }
            ]
          },
          {
            id: 'soccer-cutting-inside-vs-wide',
            title: 'Cutting Inside vs Staying Wide',
            description: 'Choose the right wide attack based on the defense. (~8 min)',
            conceptText: [
              'Wingers have two main options when receiving wide: stay wide and cross, or cut inside to shoot or combine through the middle.',
              'Staying wide stretches the defense horizontally and creates crossing angles. It\'s best when there are targets in the box or when the full-back is overcommitted.',
              'Cutting inside drags defenders out of position and opens shooting lanes for the far post. It\'s most effective when the middle is crowded and you have a strong opposite-foot shot.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'sci-q1',
                type: 'multiple-choice',
                question: 'When should a winger stay wide instead of cutting inside?',
                options: ['When the box is packed with attackers to cross to', 'When the full-back has given them space on the flank', 'When they want to shoot with their stronger foot', 'Both A and B'],
                correctIndex: 3,
                explanation: 'Staying wide is ideal when there are targets in the box and/or the full-back has left space on the outside to exploit.'
              },
              {
                id: 'sci-q2',
                type: 'true-false',
                question: 'Cutting inside is only useful for players who want to shoot.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! Cutting inside also opens passing lanes, drags defenders away from teammates, and creates overloads in central areas.'
              },
              {
                id: 'sci-q3',
                type: 'scenario',
                question: 'A left winger receives the ball on the touchline. The opposing right back is tight, but there is open space in front toward the center. What should the winger consider?',
                options: ['Cross immediately from the touchline', 'Cut inside to exploit the open central space', 'Pass backward only', 'Dribble to the corner flag'],
                correctIndex: 1,
                explanation: 'When a defender is tight on the flank and central space opens up, cutting inside is often the best decision.'
              }
            ]
          },
          {
            id: 'soccer-creating-space',
            title: 'Creating Space for Yourself',
            description: 'Use movement, body feints, and checking to lose your marker. (~8 min)',
            conceptText: [
              'Creating space starts before you receive the ball. A quick check run toward the ball, then a sharp spin away from pressure, can shake a marker.',
              'Body feints and small changes of direction force defenders to commit. If they step one way, you explode the other way into the space they just left.',
              'Checking to the ball (coming toward the passer) is a great way to receive with your body between the ball and the defender, protecting possession.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'scs-q1',
                type: 'multiple-choice',
                question: 'What is a "check run" in soccer?',
                options: ['A sprint in behind the defense', 'A movement toward the ball to create a passing option', 'A run to the corner flag', 'A defensive recovery sprint'],
                correctIndex: 1,
                explanation: 'Checking to the ball means moving toward the passer to offer a short option, often with your body shielding the defender.'
              },
              {
                id: 'scs-q2',
                type: 'multiple-choice',
                question: 'Which technique is best for losing a tight marker before receiving a pass?',
                options: ['Standing still and calling for the ball', 'A quick feint one way, then exploding the other way', 'Sprinting directly away from the ball', 'Asking the referee to call offside'],
                correctIndex: 1,
                explanation: 'A feint draws the defender\'s momentum one direction; your explosive change of direction creates separation in the opposite direction.'
              },
              {
                id: 'scs-q3',
                type: 'fix-mistake',
                question: '"Creating space only matters for attackers — defenders don\'t need space." What\'s wrong?',
                options: ['Nothing, only attackers create space', 'Defenders also create space by drawing pressure before passing out from the back', 'Space is only created by the goalkeeper', 'Defenders always have space'],
                correctIndex: 1,
                explanation: 'Defenders create space too — by baiting pressure, playing one-touch, or moving the ball quickly to find a teammate in more room.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'soccer-tactics',
    difficulty: 'intermediate',
    title: 'Tactical Concepts',
    description: 'Master pressing, possession, and how modern teams attack and defend.',
    color: 'primary',
    units: [
      {
        id: 'soccer-pressing',
        title: 'Pressing & Defending',
        icon: '🔥',
        requiredXp: 100,
        lessons: [
          {
            id: 'soccer-pressing-basics',
            title: 'High Press vs Low Block',
            description: 'Two opposite defensive philosophies.',
            conceptText: [
              'A high press means your team pushes up and tries to win the ball in the opponent\'s half. The goal: force errors near their goal and score quickly.',
              'A low block is the opposite — your team drops deep, stays compact, and denies space. You invite the opponent to have the ball and look to counter-attack.',
              'The key to pressing is triggers — specific moments when the press is activated: a poor touch, a backward pass, a player receiving with their back to goal. Without triggers, pressing becomes aimless chasing.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'spr-q1',
                type: 'multiple-choice',
                question: 'Which of these is an example of a pressing trigger?',
                options: ['The opponent completes a forward pass', 'A defender receives the ball with their back to goal', 'The opposing team scores a goal', 'The ball goes out for a throw-in'],
                correctIndex: 1,
                explanation: 'A pressing trigger is a cue to press aggressively. A player receiving with their back to goal is vulnerable — they can\'t see forward and are easier to dispossess.'
              },
              {
                id: 'spr-q2',
                type: 'fix-mistake',
                question: '"A low block is a passive strategy — the team just sits back and hopes for the best." What\'s wrong?',
                options: ['Nothing, a low block is just parking the bus', 'A low block is an organized, active defensive strategy designed to deny space and launch counter-attacks', 'Low blocks don\'t involve any defending', 'A low block means letting the opponent score'],
                correctIndex: 1,
                explanation: 'A low block is highly organized. The team defends compactly, cuts passing lanes, and waits for moments to counter-attack. It requires discipline and tactical intelligence, not passivity.'
              },
              {
                id: 'spr-q3',
                type: 'diagram-identify',
                question: 'This team\'s defensive line is pushed up near the halfway line, with forwards pressing the opponent\'s defenders. What style is this?',
                diagram: {
                  title: '',
                  sport: 'soccer',
                  players: [
                    { x: 50, y: 55, type: 'offense', label: 'GK' },
                    { x: 20, y: 42, type: 'offense', label: 'LB' },
                    { x: 38, y: 44, type: 'offense', label: 'CB' },
                    { x: 62, y: 44, type: 'offense', label: 'CB' },
                    { x: 80, y: 42, type: 'offense', label: 'RB' },
                    { x: 50, y: 32, type: 'offense', label: 'DM' },
                    { x: 35, y: 25, type: 'offense', label: 'CM' },
                    { x: 65, y: 25, type: 'offense', label: 'CM' },
                    { x: 20, y: 12, type: 'offense', label: 'LW' },
                    { x: 50, y: 10, type: 'offense', label: 'ST' },
                    { x: 80, y: 12, type: 'offense', label: 'RW' },
                  ],
                  routes: [
                    { fromX: 50, fromY: 10, toX: 45, toY: 5, type: 'solid' },
                    { fromX: 20, fromY: 12, toX: 15, toY: 7, type: 'solid' },
                    { fromX: 80, fromY: 12, toX: 85, toY: 7, type: 'solid' },
                  ]
                },
                options: ['Low block', 'High press', 'Counter-attack', 'Zonal marking'],
                correctIndex: 1,
                explanation: 'The entire team is pushed high up the pitch with the forwards actively pressing the opposition. This is a high press — the defensive line is near halfway, compressing the space.'
              }
            ]
          },
          {
            id: 'soccer-gegenpressing',
            title: 'Gegenpressing',
            description: 'Win the ball back immediately after losing it.',
            conceptText: [
              'Gegenpressing (German for "counter-pressing") means the moment your team loses the ball, everyone nearby immediately swarms to win it back — within 5 seconds if possible.',
              'The idea originated in German football and has become one of the most influential tactical concepts in modern football. The best moment to win the ball is immediately after your team just lost it.',
              'Why does it work? When you lose the ball, the opponent is transitioning — they\'re not organized yet. If you press instantly, you catch them out of shape and can create a chance close to their goal.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'sgp-q1',
                type: 'multiple-choice',
                question: 'How does gegenpressing differ from a traditional high press?',
                options: ['A high press is constant; gegenpressing is triggered specifically by losing possession', 'There is no difference — they are the same thing', 'Gegenpressing is only used in the defensive third', 'A high press requires more players than gegenpressing'],
                correctIndex: 0,
                explanation: 'A high press is a general defensive strategy of pushing up the pitch. Gegenpressing is specifically triggered the instant your team loses the ball — it\'s reactive and intense.'
              },
              {
                id: 'sgp-q2',
                type: 'multiple-choice',
                question: 'Why is the moment right after losing the ball the best time to counter-press?',
                options: ['The referee gives more leniency for tackles', 'The opponent hasn\'t organized their shape yet and is vulnerable', 'Your players have more energy because they just had the ball', 'The offside rule doesn\'t apply during transitions'],
                correctIndex: 1,
                explanation: 'When a team wins the ball, they\'re shifting from defense to attack — they\'re not in shape yet. Pressing immediately catches them disorganized and near their own goal.'
              },
              {
                id: 'sgp-q3',
                type: 'identify',
                question: 'A team loses the ball near the opponent\'s penalty area. Instead of retreating, five players immediately swarm the ball carrier and win it back in 3 seconds. What tactic is this?',
                options: ['Zonal pressing', 'Gegenpressing', 'Man-marking', 'Dropping into a low block'],
                correctIndex: 1,
                explanation: 'This is textbook gegenpressing — immediately swarming after losing possession to win the ball back before the opponent can transition. The key is the instant reaction, not retreating.'
              }
            ]
          }
        ]
      },
      {
        id: 'soccer-set-pieces',
        title: 'Set Pieces',
        icon: '🎯',
        requiredXp: 150,
        lessons: [
          {
            id: 'soccer-corners-fks',
            title: 'Corners & Free Kicks',
            description: 'Dead ball situations that win and lose games.',
            conceptText: [
              'Set pieces account for roughly 30% of all goals in professional football. Corners, free kicks, and throw-ins are rehearsed situations where teams can use pre-planned routines.',
              'Corner kicks can be delivered in-swinging (curling toward goal) or out-swinging (curling away). In-swingers are more dangerous because the ball moves toward goal. Teams set up blockers and runners to create space.',
              'Free kicks near the box can be shot directly at goal or used as a crossing opportunity. Teams often set up elaborate routines — dummy runs, short passes, and decoy players to confuse the defensive wall.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'ssp-q1',
                type: 'multiple-choice',
                question: 'Why are in-swinging corners generally considered more dangerous than out-swingers?',
                options: ['They travel faster through the air', 'The ball curves toward goal, so any touch can deflect it in', 'Defenders are not allowed to head in-swingers', 'The goalkeeper must stay on the line for in-swingers'],
                correctIndex: 1,
                explanation: 'An in-swinging corner curves toward the goal, meaning any touch from an attacker or deflection can result in a goal. Defenders running toward their own goal also find them harder to clear.'
              },
              {
                id: 'ssp-q2',
                type: 'multiple-choice',
                question: 'Teams that excel at set pieces use "blockers" during corners. What is a blocker\'s role?',
                options: ['To block the goalkeeper\'s view of the ball', 'To stand in the path of defenders and create space for attacking runners', 'To head the ball away from danger', 'To take the corner kick itself'],
                correctIndex: 1,
                explanation: 'Blockers position themselves to impede defenders\' runs, creating space for designated attackers to get free and meet the delivery. It\'s a subtle but crucial part of set-piece coaching.'
              },
              {
                id: 'ssp-q3',
                type: 'fix-mistake',
                question: '"Set pieces are mostly luck — there\'s not much a coach can do to improve them." What\'s wrong?',
                options: ['Nothing, set pieces are inherently random', 'Set pieces are heavily rehearsed with specific runs, blockers, and delivery targets', 'Only free kicks can be practiced, not corners', 'Coaches only work on defensive set pieces'],
                correctIndex: 1,
                explanation: 'Elite teams rehearse set pieces extensively. They plan specific runs, blocking movements, delivery zones, and decoy movements to create advantages from dead ball situations.'
              }
            ]
          },
          {
            id: 'soccer-penalty-kicks',
            title: 'Penalties & Shootouts',
            description: 'The most high-pressure moment in football.',
            conceptText: [
              'A penalty kick is awarded when a foul is committed inside the penalty area. The ball is placed on the penalty spot (12 yards from goal), and it\'s one player vs the goalkeeper.',
              'Penalty techniques vary: power (blast it into the corner), placement (side-foot with precision), or the Panenka (a cheeky chip down the middle while the goalkeeper dives to a side).',
              'In a penalty shootout, each team takes 5 kicks. If still tied, it goes to sudden death. The team that goes first historically wins about 60% of shootouts — the pressure of needing to match is real.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'spen-q1',
                type: 'multiple-choice',
                question: 'A team is awarded a penalty. Where exactly is the ball placed?',
                options: ['On the six-yard box line', 'On the penalty spot, 12 yards from goal', 'Anywhere inside the penalty area', 'On the edge of the penalty area'],
                correctIndex: 1,
                explanation: 'The penalty spot is a fixed point 12 yards (approximately 11 meters) from the center of the goal line. Every penalty is taken from this exact spot.'
              },
              {
                id: 'spen-q2',
                type: 'identify',
                question: 'A player gently chips the ball down the center of the goal as the goalkeeper dives to one side. What is this technique called?',
                options: ['A knuckleball', 'A Panenka', 'A trivela', 'A rabona'],
                correctIndex: 1,
                explanation: 'The Panenka is a soft chip down the middle, exploiting the fact that goalkeepers almost always dive to one side. It requires nerve — if the keeper doesn\'t dive, it\'s an easy save.'
              },
              {
                id: 'spen-q3',
                type: 'multiple-choice',
                question: 'Statistically, the team that shoots first in a penalty shootout wins about 60% of the time. Why?',
                options: ['The pitch conditions deteriorate for the second team', 'The second team faces more psychological pressure of "needing to score to stay alive"', 'The goalkeeper gets tired from diving', 'The first team gets to choose which end to shoot at'],
                correctIndex: 1,
                explanation: 'The second team is more often in a "must-score" situation. Knowing you\'ll be eliminated if you miss creates additional psychological pressure that affects performance.'
              }
            ]
          }
        ]
      }
    ]
  },
  // ── New: Attacking Principles (Intermediate) ─────────────
  {
    id: 'soccer-attacking',
    difficulty: 'intermediate' as const,
    title: 'Attacking Play',
    description: 'Build-up play, creating chances, and finishing moves.',
    color: 'primary',
    units: [
      {
        id: 'soccer-buildup',
        title: 'Build-Up Play',
        icon: '🎯',
        requiredXp: 120,
        lessons: [
          {
            id: 'soccer-playing-out',
            title: 'Playing Out from the Back',
            description: 'How modern teams build attacks from their own goal.',
            conceptText: [
              'Playing out from the back means the goalkeeper and defenders pass the ball forward rather than hoofing it long. It\'s risky but creates numerical advantages in midfield.',
              'The key shapes: defenders spread wide, the goalkeeper acts as an extra player, and midfielders drop to create passing triangles. The goal is to draw the press and then play through it.',
              'Teams like Manchester City and Barcelona made this the dominant style. When done well, the opposition presses and gets bypassed, leaving acres of space behind them.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'soc-build-q1',
                type: 'multiple-choice' as const,
                question: 'Why do teams play out from the back instead of going long?',
                options: [
                  'Long balls are against the rules',
                  'It creates numerical advantages in midfield when the press is beaten',
                  'Goalkeepers aren\'t allowed to kick long anymore',
                  'It guarantees possession for the full 90 minutes',
                ],
                correctIndex: 1,
                explanation: 'Playing out from the back draws opponents forward. Once you bypass the press with short passes, you have more players than the opponent in midfield, creating attacking overloads.',
              },
              {
                id: 'soc-build-match1',
                type: 'matching' as const,
                question: 'Match each role in the build-up with their responsibility.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Goalkeeper', 'Act as an extra outfield player for short passes'],
                  ['Center-backs', 'Split wide to stretch the opposition press'],
                  ['Defensive midfielder', 'Drop between center-backs to receive'],
                  ['Fullbacks', 'Push high to provide width in attack'],
                ],
                explanation: 'Modern build-up play requires every player to have a specific role. The goalkeeper is the first passer, and the fullbacks become attackers.',
              },
              {
                id: 'soc-build-sit1',
                type: 'situational' as const,
                question: 'What should the center-back do?',
                situationContext: 'Goal kick | Opponent pressing high | Score is 0-0',
                options: [
                  'Blast it long to the striker',
                  'Play short to the goalkeeper who switches to the other center-back',
                  'Dribble through the pressing forwards',
                  'Wait until the referee forces a drop ball',
                ],
                correctIndex: 1,
                explanation: 'Playing short to the GK who switches play moves the ball away from the press. The other CB receives in space because the pressing team committed numbers to one side.',
              },
            ],
          },
          {
            id: 'soccer-counter-attack',
            title: 'The Counterattack',
            description: 'Turning defense into a devastating attack.',
            conceptText: [
              'A counterattack is a rapid transition from defense to attack, exploiting the space left behind by an opponent who has committed numbers forward.',
              'The key ingredients: win the ball, play forward quickly (ideally in 3-4 passes), and attack before the defense can recover. Speed and directness trump possession.',
              'Great counter-attacking teams like Real Madrid and Liverpool are lethal because they have fast forwards who can cover 40-50 yards in seconds. It\'s the great equalizer against possession-dominant sides.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'soc-counter-sit1',
                type: 'situational' as const,
                question: 'Your midfielder just won the ball in your own half. What\'s the best option?',
                situationContext: 'Opponent has 8 players in your half | Your winger is free on the right | 1-1 in 75th minute',
                options: [
                  'Pass it back to the goalkeeper to slow things down',
                  'Play a quick long ball to the free winger and sprint forward',
                  'Dribble sideways to keep possession',
                  'Call for a substitution',
                ],
                correctIndex: 1,
                explanation: 'With 8 opponents committed forward and a free winger, this is a textbook counter-attack opportunity. Play direct and attack the space before they can recover.',
              },
              {
                id: 'soc-counter-q1',
                type: 'true-false' as const,
                question: 'Counterattacks work best against teams that sit deep and defend.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! Counterattacks exploit teams that push forward and leave space behind. Deep-sitting teams don\'t leave that space, making counters ineffective against them.',
              },
              {
                id: 'soc-counter-q2',
                type: 'multiple-choice' as const,
                question: 'What makes a counterattack different from a normal attack?',
                options: [
                  'More passing is involved',
                  'It starts from winning possession and attacks rapidly before the defense resets',
                  'Only forwards can participate',
                  'It must result in a shot within 5 seconds',
                ],
                correctIndex: 1,
                explanation: 'A counterattack is defined by the transition — winning the ball and immediately attacking the disorganized defense before they can get back into shape.',
              },
            ],
          },
        ],
      },
    ],
  },
  // ── New: Set Pieces (Beginner) ───────────────────────────
  {
    id: 'soccer-set-pieces',
    difficulty: 'beginner' as const,
    title: 'Set Pieces',
    description: 'Corners, free kicks, and the goals that come from dead-ball situations.',
    color: 'primary',
    units: [
      {
        id: 'soccer-dead-balls',
        title: 'Dead Ball Mastery',
        icon: '📐',
        requiredXp: 30,
        lessons: [
          {
            id: 'soccer-corners-fks-advanced',

            title: 'Corners & Free Kicks',
            description: 'Approximately 30% of goals come from set pieces.',
            conceptText: [
              'A corner kick happens when the defending team plays the ball over their own goal line. The attacking team kicks from the corner arc — it\'s a huge scoring opportunity!',
              'Free kicks come in two types: direct (you can score straight from the kick) and indirect (someone else must touch the ball first). The ref shows indirect by raising their arm.',
              'Top teams spend hours practicing set piece routines. Short corners, near-post flick-ons, and decoy runners are all designed to create confusion in the box and find a free header.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'soc-sp-q1',
                type: 'multiple-choice' as const,
                question: 'When is a corner kick awarded?',
                options: [
                  'When the attacking team kicks it over the goal line',
                  'When the defending team plays the ball over their own goal line',
                  'After every foul in the box',
                  'At the start of each half',
                ],
                correctIndex: 1,
                explanation: 'A corner kick is awarded when the defending team is the last to touch the ball before it crosses the goal line (without a goal being scored).',
              },
              {
                id: 'soc-sp-match1',
                type: 'matching' as const,
                question: 'Match each dead ball situation with its restart.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Ball crosses sideline', 'Throw-in'],
                  ['Foul outside the box', 'Direct free kick'],
                  ['Defender plays ball over own goal line', 'Corner kick'],
                  ['Attacker plays ball over opponent goal line', 'Goal kick'],
                ],
                explanation: 'Each way the ball goes out of play has a specific restart. Learning these is fundamental to understanding the flow of a soccer match.',
              },
              {
                id: 'soc-sp-tf1',
                type: 'true-false' as const,
                question: 'You can score directly from a throw-in without anyone else touching the ball.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! A goal cannot be scored directly from a throw-in. Another player must touch the ball first. If thrown directly into the opponent\'s goal, a goal kick is awarded.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'soccer-history',
    difficulty: 'beginner',
    title: 'History & Culture',
    description: 'The stories, moments, and traditions that make soccer the world\'s game.',
    color: 'primary',
    units: [
      {
        id: 'soccer-world-cup',
        title: 'The World Cup',
        icon: '🏆',
        requiredXp: 0,
        lessons: [
          {
            id: 'soccer-world-cup-intro',
            title: 'The Biggest Stage',
            description: 'What makes the World Cup special.',
            conceptText: [
              'The FIFA World Cup is held every four years and is the most-watched sporting event on Earth — bigger than the Super Bowl and the Olympics combined.',
              'It started in 1930 with just 13 teams. Today, the best national teams on the planet battle through a month of group stage and knockout matches for one trophy.',
              'Winning it is the hardest thing to do in soccer. A player might only get two or three real chances in an entire career.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'soccer-wc-q1',
                type: 'multiple-choice',
                question: 'The World Cup is held every how many years?',
                options: ['Every 2 years', 'Every 4 years', 'Every 3 years', 'Every 5 years'],
                correctIndex: 1,
                explanation: 'Like the Olympics, the World Cup runs on a four-year cycle — which makes winning one even more special. Teams get very few chances in a player\'s career.',
              },
              {
                id: 'soccer-wc-q2',
                type: 'true-false',
                question: 'The World Cup is the most-watched sporting event in the world.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. The World Cup final regularly pulls more than 1 billion viewers — no other single sporting event comes close.',
              },
            ],
          },
          {
            id: 'soccer-world-cup-2026',
            title: 'Tournament Format',
            description: 'How the modern 48-team World Cup is structured.',
            conceptText: [
              'The modern World Cup features 48 teams — an expansion from the 32-team format used from 1998 through 2022. More nations qualify, and the tournament runs 104 matches in total.',
              'The group stage is 12 groups of four teams. Each team plays the other three in its group once, with three points for a win and one for a draw.',
              'The top two from each group, plus the eight best third-place finishers, advance to a 32-team knockout round. One loss in the knockouts and you\'re out.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'soccer-wc26-q1',
                type: 'multiple-choice',
                question: 'How many groups are there in the group stage?',
                options: ['8', '10', '12', '16'],
                correctIndex: 2,
                explanation: '12 groups of four teams each, which is how 48 nations fit into the opening round.',
              },
              {
                id: 'soccer-wc26-q2',
                type: 'multiple-choice',
                question: 'How many teams compete in the modern World Cup?',
                options: ['24', '32', '48', '64'],
                correctIndex: 2,
                explanation: '48 teams — a major expansion from the 32-team format used from 1998 through 2022. More nations, more matches, more chaos.',
              },
              {
                id: 'soccer-wc26-q3',
                type: 'true-false',
                question: 'Every team that finishes third in their group is eliminated.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False. The eight best third-place finishers across all 12 groups advance into the Round of 32. Third place is no longer automatic elimination.',
              },
            ],
          },
          {
            id: 'soccer-world-cup-knockouts',
            title: 'Knockout Drama',
            description: 'Why one match can change a nation.',
            conceptText: [
              'The group stage is a marathon — the knockout stage is a sprint. One bad half, one missed penalty, one moment of magic, and it\'s over.',
              'If knockout matches are tied after 90 minutes, two 15-minute periods of extra time are played. Still tied? It goes to a penalty shootout: five shots per side from 12 yards.',
              'Penalty shootouts are pure pressure. The best goalkeepers study opponents for weeks. The best takers blank everything out and trust their routine.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'soccer-wc-ko-q1',
                type: 'multiple-choice',
                question: 'How long is extra time in a knockout match?',
                options: ['One 10-minute period', 'Two 15-minute periods', 'One 30-minute period', 'Two 20-minute periods'],
                correctIndex: 1,
                explanation: 'Two halves of 15 minutes each — 30 minutes total. Teams switch ends between them. If the score is still level, it\'s on to penalties.',
              },
              {
                id: 'soccer-wc-ko-q2',
                type: 'multiple-choice',
                question: 'From how many yards are penalty kicks taken?',
                options: ['10 yards', '12 yards', '15 yards', '18 yards'],
                correctIndex: 1,
                explanation: '12 yards (11 meters) from goal. It\'s the keeper vs the taker — and at the World Cup, the whole world is watching.',
              },
              {
                id: 'soccer-wc-ko-q3',
                type: 'true-false',
                question: 'In a penalty shootout, each team takes five shots before going to sudden death.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True. Each side takes five penalties. If the scores are level after that, it goes to sudden death — one shot each until someone misses.',
              },
            ],
          },
        ],
      },
    ],
  },
];

export function getSoccerTotalLessons(): number {
  return soccerLearningPaths.reduce((total, path) =>
    total + path.units.reduce((unitTotal, unit) =>
      unitTotal + unit.lessons.length, 0), 0);
}

export function findSoccerLesson(lessonId: string): { path: LearningPath; unit: Unit; lesson: Lesson } | null {
  for (const path of soccerLearningPaths) {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        if (lesson.id === lessonId) {
          return { path, unit, lesson };
        }
      }
    }
  }
  return null;
}

export function getNextSoccerLesson(currentLessonId: string, xp: number): Lesson | null {
  let found = false;
  for (const path of soccerLearningPaths) {
    for (const unit of path.units) {
      if (xp < unit.requiredXp) continue;
      for (const lesson of unit.lessons) {
        if (found) return lesson;
        if (lesson.id === currentLessonId) found = true;
      }
    }
  }
  return null;
}
